import { useState, useEffect, useCallback } from 'react';

export interface OfflineChecklist {
  id: string;
  title: string;
  items: Array<{ text: string; note?: string }>;
  checkedItems: number[];
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = 'offline_checklists';

export const useOfflineChecklists = () => {
  const [checklists, setChecklists] = useState<OfflineChecklist[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setChecklists(JSON.parse(stored));
      } catch {
        console.error('Failed to parse offline checklists');
      }
    }
  }, []);

  // Persist to localStorage whenever checklists change
  const persistChecklists = useCallback((newChecklists: OfflineChecklist[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newChecklists));
    setChecklists(newChecklists);
  }, []);

  const saveChecklist = useCallback((
    title: string,
    items: Array<{ text: string; note?: string }>,
    checkedItems: number[] = [],
    existingId?: string
  ): string => {
    const now = new Date().toISOString();
    const id = existingId || crypto.randomUUID();
    
    const checklist: OfflineChecklist = {
      id,
      title,
      items,
      checkedItems,
      createdAt: existingId ? checklists.find(c => c.id === existingId)?.createdAt || now : now,
      updatedAt: now,
    };

    const updated = existingId
      ? checklists.map(c => c.id === existingId ? checklist : c)
      : [checklist, ...checklists];
    
    persistChecklists(updated);
    return id;
  }, [checklists, persistChecklists]);

  const deleteChecklist = useCallback((id: string) => {
    const updated = checklists.filter(c => c.id !== id);
    persistChecklists(updated);
  }, [checklists, persistChecklists]);

  const getChecklist = useCallback((id: string): OfflineChecklist | undefined => {
    return checklists.find(c => c.id === id);
  }, [checklists]);

  const updateCheckedItems = useCallback((id: string, checkedItems: number[]) => {
    const updated = checklists.map(c => 
      c.id === id ? { ...c, checkedItems, updatedAt: new Date().toISOString() } : c
    );
    persistChecklists(updated);
  }, [checklists, persistChecklists]);

  return {
    checklists,
    saveChecklist,
    deleteChecklist,
    getChecklist,
    updateCheckedItems,
  };
};
